/**
 * Panel Context Menu — dependency-free floating context menu (D1 decision).
 *
 * Ported from the reference app's ContextMenuProvider: one provider per
 * document renders a viewport-clamped floating menu with `showMenu(content,
 * x, y)` / `hideMenu()`, suppresses the native browser menu outside its own
 * root, and closes on click. Styles live in `theme/context-menu.css`.
 */

import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from 'react';
import { useViewportClampedPosition } from './useViewportClampedPosition';
import '../theme/context-menu.css';

type ContextMenuState = {
    visible: boolean;
    x: number;
    y: number;
    content: ReactNode | null;
};

type ContextMenuContextType = {
    showMenu: (content: ReactNode, x: number, y: number) => void;
    hideMenu: () => void;
};

const ContextMenuContext = createContext<ContextMenuContextType | undefined>(
    undefined,
);

export const useContextMenu = () => {
    const ctx = useContext(ContextMenuContext);
    if (!ctx)
        throw new Error(
            'useContextMenu must be used within ContextMenuProvider',
        );
    return ctx;
};

export const ContextMenuProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [state, setState] = useState<ContextMenuState>({
        visible: false,
        x: 0,
        y: 0,
        content: null,
    });

    const showMenu = (content: ReactNode, x: number, y: number) => {
        setState({ visible: true, x, y, content });
    };

    const hideMenu = () =>
        setState((s) => ({ ...s, visible: false, content: null }));

    // App-wide floating-menu rule: keep the menu inside the main window even
    // when it was opened near a screen edge (repositioned before paint).
    const anchor = state.visible ? { x: state.x, y: state.y } : null;
    const { ref: menuClampedRef, left, top } =
        useViewportClampedPosition<HTMLDivElement>(anchor);

    // Suppress the native browser context menu app-wide; app-defined menus
    // open via their own onContextMenu handlers, which already preventDefault.
    useEffect(() => {
        const suppressNativeMenu = (e: MouseEvent) => {
            if (
                e.target instanceof Element &&
                e.target.closest('.context-menu-root')
            )
                return;
            e.preventDefault();
        };
        window.addEventListener('contextmenu', suppressNativeMenu);
        return () =>
            window.removeEventListener('contextmenu', suppressNativeMenu);
    }, []);

    return (
        <ContextMenuContext.Provider value={{ showMenu, hideMenu }}>
            {children}
            {state.visible && (
                <div
                    ref={menuClampedRef}
                    className="context-menu-root"
                    style={{
                        top,
                        left,
                    }}
                    onClick={hideMenu}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        hideMenu();
                    }}
                >
                    {state.content}
                </div>
            )}
        </ContextMenuContext.Provider>
    );
};
